
import { defineComponent, ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useQuery, useResult, useMutation } from "@vue/apollo-composable";
import { logErrorMessages } from "@vue/apollo-util";

import EntitiesList from "@/components/list/EntitiesList.vue";
import ButtonIcon from "@/components/ButtonIcon.vue";

import AddEmployees from "@/components/employee/AddEmployees.vue";
import EditEmployees from "@/components/employee/EditEmployees.vue";

import { GET_DETAILED_EMPLOYEES } from "@/graphql/user/queries";
import { UPDATE_USER } from "@/graphql/user/mutations";

import enUK from "@/locales/en-UK.json";
import { User } from "@/helpers/user/typesDefinition";
import { formatPrice } from "@/helpers/utils";
import { useToast } from "vue-toastification";

function formatAvatar(
  _value: undefined,
  _name: string,
  user: User,
): { name: string; avatarUrl?: string } {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ");
  return {
    name: fullName,
    avatarUrl: user.avatarUrl,
  };
}

export default defineComponent({
  setup() {
    const { t } = useI18n({
      useScope: "global",
      inheritLocale: true,
    });

    const toast = useToast();

    const { result, loading, onError, refetch } = useQuery(
      GET_DETAILED_EMPLOYEES,
    );

    const employees = useResult(result, [], (data) => {
      return (data?.users ?? []).map((user: User) => {
        const fullName = [user.firstName, user.lastName]
          .filter(Boolean)
          .join(" ");

        return {
          ...user,
          avatar: {
            name: fullName,
            avatarUrl: user.avatarUrl,
          },
          fullname: fullName,
          defaultRole: user.defaultRole?.name ?? "-",
          defaultHourlyRate: user.defaultRole?.hourlyRate ?? null,
          defaultRoleId: user.defaultRole?.id,
          workDayHours: user.workDayHours ?? [],
          region: user.region ?? "-",
        };
      });
    });

    const isAddModalVisible = ref(false);
    const isEditModalVisible = ref(false);
    const selectedEmployee = ref<User | null>(null);
    const checkBoxes = ref<boolean[]>([]);

    const {
      mutate: archiveEmployee,
      onDone: onArchiveDone,
      onError: onArchiveError,
      loading: archivingEmployee,
    } = useMutation(UPDATE_USER);

    onArchiveError((error) => {
      logErrorMessages(error);
      toast.error(t("errors.archiveEmployee"));
    });

    onArchiveDone(() => {
      toast.success(t("success.archiveEmployee"));
      refetch();
      checkBoxes.value = [];
    });

    const selectedEmployees = computed(() => {
      const selected: User[] = [];
      checkBoxes.value.forEach((checked, index) => {
        if (checked && employees.value[index]) {
          selected.push(employees.value[index]);
        }
      });
      return selected;
    });

    const formatters = {
      avatar: formatAvatar,
      defaultHourlyRate: formatPrice,
      workDayHours: (value: number[] | undefined) => {
        if (!value || !Array.isArray(value)) return "-";
        const total = value.reduce((sum, hours) => sum + (hours || 0), 0);
        return total.toString();
      },
    };

    function showAddModal(): void {
      isAddModalVisible.value = true;
    }

    function closeAddModal(): void {
      isAddModalVisible.value = false;
    }

    function showEditModal(employee: User): void {
      selectedEmployee.value = employee;
      isEditModalVisible.value = true;
    }

    function closeEditModal(): void {
      isEditModalVisible.value = false;
      selectedEmployee.value = null;
    }

    function showArchiveModal(): void {
      if (selectedEmployees.value.length > 0) {
        selectedEmployees.value.forEach((employee) => {
          archiveEmployee({
            id: employee.id,
            active: false,
          });
        });
      }
    }

    function handleEmployeeClick(employee: User): void {
      showEditModal(employee);
    }

    onError((error) => {
      logErrorMessages(error);
    });

    return {
      t,
      employees,
      loading,
      isAddModalVisible,
      isEditModalVisible,
      selectedEmployee,
      checkBoxes,
      selectedEmployees,
      showAddModal,
      closeAddModal,
      showEditModal,
      closeEditModal,
      showArchiveModal,
      handleEmployeeClick,
      formatters,
      archivingEmployee,
    };
  },
  data() {
    return {
      enUK,
    };
  },
  components: {
    EntitiesList,
    ButtonIcon,
    AddEmployees,
    EditEmployees,
  },
  provide: {
    columnsOrder: {
      checkbox: null,
      avatar: null,
      fullname: null,
      email: null,
      defaultRole: null,
      defaultHourlyRate: null,
      workDayHours: null,
      region: null,
    },
  },
});
